import { useEffect, useState } from 'react';
import { peopleGateway } from '../../index';
import { AddWorkLocationDialog } from '../../components/AddWorkLocationDialog';
import { locationsAfterAdd } from '../../components/locationsAfterAdd';
import { HelpTooltip, WORK_LOCATION_ACKNOWLEDGEMENT_TOOLTIP } from '../../../../shared/components/HelpTooltip';
import {
  employmentTypeLabels,
  workArrangementLabels,
  type CompanyWorkLocationOption,
  type DepartmentOption,
  type EmployeeResponse,
  type EmployeeWorkLocationResponse,
  type JobTitleOption,
} from '../../types';

interface SectionProps {
  employee: EmployeeResponse;
  onEmployeeUpdated?: ((updated: EmployeeResponse) => void) | undefined;
}

/** Work Details (EPN-152, slices 3a and 3b): department and job title are picked, or added right here ("pick or add"), so there
 * is no separate company-wide setup page; Employment Type is a fixed list. All are saved through the existing employee update.
 * Discipline, Approving Officer, WC Class Code and Role are deliberately not here yet (their lists are still undefined), so they
 * are not shown as if they worked. The server decides what is valid; adding needs `employees.write` and a refusal is shown. */
export function WorkDetailsSection({ employee, onEmployeeUpdated }: SectionProps) {
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [jobTitles, setJobTitles] = useState<JobTitleOption[]>([]);
  const [departmentId, setDepartmentId] = useState(employee.departmentId ?? '');
  const [jobTitleId, setJobTitleId] = useState(employee.jobTitleId ?? '');
  const [employmentType, setEmploymentType] = useState(employee.employmentType ? String(employee.employmentType) : '');
  const [adding, setAdding] = useState<'department' | 'jobTitle' | null>(null);
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // EPN-216: read-only. 'loading' | 'unavailable' | an ISO timestamp | null (= not yet acknowledged by the employee).
  const [acknowledgement, setAcknowledgement] = useState<'loading' | 'unavailable' | string | null>('loading');

  useEffect(() => {
    let cancelled = false;
    void peopleGateway
      .getEmployeeWorkLocationAcknowledgement(employee.id)
      .then((r) => {
        if (!cancelled) setAcknowledgement(r.acknowledgedAtUtc);
      })
      .catch(() => {
        if (!cancelled) setAcknowledgement('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [employee.id]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([peopleGateway.listDepartments(), peopleGateway.listJobTitles()])
      .then(([d, j]) => {
        if (!cancelled) {
          setDepartments(d.filter((x) => x.isActive));
          setJobTitles(j.filter((x) => x.isActive));
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load departments and job titles.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const titlesForDepartment = jobTitles.filter((j) => !departmentId || !j.departmentId || j.departmentId === departmentId);

  function startAdding(kind: 'department' | 'jobTitle') {
    setAdding(kind);
    setNewName('');
    setAddError(null);
  }

  function cancelAdding() {
    setAdding(null);
    setNewName('');
    setAddError(null);
  }

  async function addNew() {
    const name = newName.trim();
    if (!name) {
      setAddError(adding === 'department' ? 'Enter a department name.' : 'Enter a job title.');
      return;
    }
    setAddError(null);
    setAddBusy(true);
    try {
      if (adding === 'department') {
        const created = await peopleGateway.createDepartment(name);
        setDepartments((prev) => (prev.some((d) => d.id === created.id) ? prev : [...prev, created]));
        setDepartmentId(created.id);
        setJobTitleId('');
      } else {
        const created = await peopleGateway.createJobTitle(name, departmentId || null);
        setJobTitles((prev) => (prev.some((j) => j.id === created.id) ? prev : [...prev, created]));
        setJobTitleId(created.id);
      }
      cancelAdding();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Could not add it. You may not have permission to add to this list.');
    } finally {
      setAddBusy(false);
    }
  }

  async function save() {
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      // The server replaces the whole personal record, so every existing value is sent back unchanged.
      const updated = await peopleGateway.updateEmployee(employee.id, {
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        dateOfBirth: employee.dateOfBirth,
        ssnLast4: employee.ssnLast4,
        departmentId: departmentId || null,
        jobTitleId: jobTitleId || null,
        hireDate: employee.hireDate,
        employeeNumber: employee.employeeNumber,
        phone: employee.phone ?? null,
        gender: employee.gender ?? null,
        isNewHire: employee.isNewHire ?? null,
        employmentType: employmentType ? Number(employmentType) : null,
      });
      onEmployeeUpdated?.(updated);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save Work Details.');
    } finally {
      setSaving(false);
    }
  }

  const adder = (kind: 'department' | 'jobTitle', noun: string) =>
    adding === kind ? (
      <div role="group" aria-label={`Add a new ${noun}`} style={{ marginTop: 6 }}>
        <input
          aria-label={`New ${noun} name`}
          value={newName}
          maxLength={100}
          autoFocus
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void addNew();
            } else if (e.key === 'Escape') cancelAdding();
          }}
        />
        <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => void addNew()} disabled={addBusy}>
            {addBusy ? 'Adding…' : `Add ${noun}`}
          </button>
          <button type="button" onClick={cancelAdding} disabled={addBusy}>Cancel</button>
        </div>
        {addError && <p className="error" role="alert">{addError}</p>}
      </div>
    ) : (
      <button type="button" className="link-button" style={{ marginTop: 4 }} onClick={() => startAdding(kind)} disabled={adding !== null}>
        + Add new {noun}
      </button>
    );

  return (
    <section className="profile-section">
      <h3 className="profile-section-title">Work Details</h3>
      <div className="profile-field-grid">
        <div>
          <label>
            Department
            <select value={departmentId} onChange={(e) => { setDepartmentId(e.target.value); setJobTitleId(''); }}>
              <option value="">Not set</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </label>
          {adder('department', 'department')}
        </div>
        <div>
          <label>
            Job Title
            <select value={jobTitleId} onChange={(e) => setJobTitleId(e.target.value)}>
              <option value="">Not set</option>
              {titlesForDepartment.map((j) => (
                <option key={j.id} value={j.id}>{j.name}</option>
              ))}
            </select>
          </label>
          {adder('jobTitle', 'job title')}
        </div>
        <label>
          Employment Type
          <select value={employmentType} onChange={(e) => setEmploymentType(e.target.value)}>
            <option value="">Not set</option>
            {Object.entries(employmentTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>Hiring Date<input value={employee.hireDate} disabled /></label>
      </div>
      <div className="profile-check-row" data-testid="work-location-acknowledgement-status">
        <strong>Work location tax acknowledgement</strong>
        <HelpTooltip label="About the work location acknowledgement" text={WORK_LOCATION_ACKNOWLEDGEMENT_TOOLTIP} />
        <span style={{ marginLeft: 8 }}>
          {acknowledgement === 'loading'
            ? 'Checking…'
            : acknowledgement === 'unavailable'
              ? 'Status unavailable.'
              : acknowledgement
                ? `Acknowledged by the employee on ${acknowledgement.slice(0, 10)}.`
                : 'Not yet acknowledged by the employee.'}
        </span>
      </div>
      {error && <p className="error">{error}</p>}
      {saved && <p className="muted" role="status">Work Details saved.</p>}
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save Work Details'}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 10 }}>
        Discipline, approving officer and WC class code will be added in a later step. Employment Type must be set before this
        employee can be payroll-ready; it does not change pay or tax calculations.
      </p>
    </section>
  );
}

const ADD_WORK_LOCATION = '__add_work_location__';

/** Work Location (EPN-152, slice 3a), from the approved SRS section C: one primary location with an effective date and a
 * permanent history, optional allowed secondary locations, and the work arrangement. The server decides what is valid. */
export function WorkLocationSection({ employee }: SectionProps) {
  const [locations, setLocations] = useState<CompanyWorkLocationOption[]>([]);
  const [current, setCurrent] = useState<EmployeeWorkLocationResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [primaryId, setPrimaryId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');
  const [arrangement, setArrangement] = useState('');
  const [homeIsWork, setHomeIsWork] = useState(false);
  const [allowed, setAllowed] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // EPN-233: which control opened the Add Work Location prompt, so the new location lands where the admin was working.
  const [addFor, setAddFor] = useState<null | 'primary' | 'allowed'>(null);
  const [addedNote, setAddedNote] = useState('');

  function apply(r: EmployeeWorkLocationResponse) {
    setCurrent(r);
    setPrimaryId(r.primary?.workLocationId ?? '');
    setEffectiveDate('');
    setReason('');
    setArrangement(r.workArrangement ? String(r.workArrangement) : '');
    setHomeIsWork(r.isHomeAddressAlsoWorkLocation ?? false);
    setAllowed(r.allowedWorkLocationIds);
  }

  useEffect(() => {
    let cancelled = false;
    void Promise.all([peopleGateway.listCompanyWorkLocations(), peopleGateway.getEmployeeWorkLocation(employee.id)])
      .then(([l, r]) => {
        if (!cancelled) {
          setLocations(l);
          apply(r);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load work locations.');
      });
    return () => {
      cancelled = true;
    };
  }, [employee.id]);

  const nameOf = (id: string) => locations.find((l) => l.id === id)?.name ?? 'Unknown location';
  const active = locations.filter((l) => l.status === 'Active');
  const primaryChanges = primaryId !== '' && primaryId !== (current?.primary?.workLocationId ?? '');

  function toggleAllowed(id: string) {
    setAllowed((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function save() {
    setError(null);
    setSaved(false);
    if (!primaryId) {
      setError('Primary Work Location is required.');
      return;
    }
    if (primaryChanges && !effectiveDate) {
      setError('Please enter an effective date for the work location change before saving.');
      return;
    }
    setSaving(true);
    try {
      const result = await peopleGateway.saveEmployeeWorkLocation(employee.id, {
        primaryWorkLocationId: primaryId,
        effectiveDate: primaryChanges ? effectiveDate : null,
        reason: primaryChanges && reason.trim() ? reason.trim() : null,
        workArrangement: arrangement ? Number(arrangement) : null,
        isHomeAddressAlsoWorkLocation: homeIsWork,
        allowedWorkLocationIds: allowed.filter((id) => id !== primaryId),
      });
      apply(result);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the work location.');
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <section className="profile-section">
        <h3 className="profile-section-title">Work Location</h3>
        <p className="error">{loadError}</p>
      </section>
    );
  }

  return (
    <section className="profile-section">
      <div className="profile-section-head">
        <h3 className="profile-section-title">Work Location</h3>
        <a className="profile-history-link" href="#work-location-history">
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M2.5 8a5.5 5.5 0 1 0 1.8-4.1M2.5 2.5v3h3M8 5v3.2l2 1.2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Work Location History
        </a>
      </div>
      <div className="profile-field-grid profile-field-grid--location">
        <label>
          <span className="field-label field-label--required">Primary Work Location</span>
          <select
            value={primaryId}
            onChange={(e) => {
              if (e.target.value === ADD_WORK_LOCATION) { setAddFor('primary'); return; }
              setPrimaryId(e.target.value);
              setAddedNote('');
            }}
          >
            <option value="">Select a location</option>
            {active.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
            {primaryId && !active.some((l) => l.id === primaryId) && <option value={primaryId}>{nameOf(primaryId)} (no longer active)</option>}
            <option value={ADD_WORK_LOCATION}>+ Add New Work Location</option>
          </select>
        </label>
        <label>
          <span className={`field-label${primaryChanges ? ' field-label--required' : ''}`}>Effective Date</span>
          <input
            type="date"
            value={effectiveDate}
            disabled={!primaryChanges}
            title={primaryChanges ? undefined : 'Needed only when the primary work location changes.'}
            onChange={(e) => setEffectiveDate(e.target.value)}
            required={primaryChanges}
          />
        </label>
        <label>
          Location Indicator
          <select value={arrangement} onChange={(e) => setArrangement(e.target.value)}>
            <option value="">Not set</option>
            {Object.entries(workArrangementLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        {primaryChanges && (
          <label>
            Reason (optional)
            <input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          </label>
        )}
      </div>

      <label className="profile-check-row">
        <input type="checkbox" checked={homeIsWork} onChange={(e) => setHomeIsWork(e.target.checked)} /> Home address is also the work location
      </label>
      <p className="muted">
        Use this when the employee works from home. The employee&apos;s Home Address will also be treated as their Work Location for payroll tax purposes.
      </p>

      <fieldset className="profile-location-list">
        <legend>Allowed Secondary Locations</legend>
        <p className="muted">
          Select other company locations this employee is allowed to work at. These locations may be used during time entry or payroll
          processing when wages need to be split by location.
        </p>
        <div className="profile-location-list__items">
          {active.filter((l) => l.id !== primaryId).map((l) => (
            <label key={l.id} className="profile-check-row">
              <input type="checkbox" checked={allowed.includes(l.id)} onChange={() => toggleAllowed(l.id)} />
              {l.name}
            </label>
          ))}
        </div>
        {active.filter((l) => l.id !== primaryId).length === 0 && <p className="muted">No other active locations. Add one here or under Settings &gt; Company &gt; Work Locations.</p>}
        <button type="button" className="link-button" onClick={() => setAddFor('allowed')}>+ Add New Work Location</button>
      </fieldset>
      {addedNote && <p className="muted" role="status">{addedNote}</p>}
      {addFor && (
        <AddWorkLocationDialog
          onClose={() => setAddFor(null)}
          onAdded={(created) => {
            const target = addFor;
            setAddFor(null);
            void locationsAfterAdd(created, locations, false).then((result) => {
              setLocations(result.locations);
              if (!result.selectable) {
                setAddedNote(`${created.name} was added but is inactive, so it cannot be assigned yet. Turn on Active for Payroll under Company Details when it is ready.`);
              } else if (target === 'primary') {
                setPrimaryId(created.id);
                setAllowed((prev) => prev.filter((x) => x !== created.id));
                setAddedNote(`${created.name} was added and selected as the primary work location. Save Work Location to apply it.`);
              } else {
                setAllowed((prev) => (prev.includes(created.id) ? prev : [...prev, created.id]));
                setAddedNote(`${created.name} was added and checked as an allowed location. Save Work Location to apply it.`);
              }
            });
          }}
        />
      )}

      {error && <p className="error">{error}</p>}
      {saved && <p className="muted" role="status">Work Location saved.</p>}
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save Work Location'}
        </button>
      </div>

      <h4 id="work-location-history" style={{ marginTop: 20 }}>Work Location History</h4>
      {current && current.history.length > 0 ? (
        <table className="profile-history-table">
          <thead>
            <tr><th>Location</th><th>Effective</th><th>Reason</th><th>Changed</th></tr>
          </thead>
          <tbody>
            {current.history.map((h) => (
              <tr key={`${h.workLocationId}-${h.effectiveFrom}`}>
                <td>{nameOf(h.workLocationId)}{h.isCurrent ? ' (current)' : ''}</td>
                <td>{h.effectiveFrom}</td>
                <td>{h.reason ?? '—'}</td>
                <td>{h.changedAtUtc.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No work location has been assigned yet.</p>
      )}
    </section>
  );
}
